// ============================================================
// FILE: src/pages/Home.jsx  →  THE FIRST PAGE (HOMEPAGE)
// PURPOSE: Everything you see on the homepage, top to bottom:
//   1. Hero (headline + trade video + floating cards)
//   2. Scrolling marquee words
//   3. Animated numbers
//   4. "Evolve Doctrine" text
//   5. Product portfolio (featured products)
//   6. Services grid
//   7. Why Choose Us
//   8. Export Process (auto-scrolling 10 steps)
//   9. Certifications
//  10. Testimonials
//  11. FAQ
//  12. Big call-to-action banner
// TIP: Most TEXT for these sections lives in src/data/site.js
// ============================================================

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowDown, ShieldCheck, Ship, Plane, Star, Globe2, ClipboardCheck, Plus, Minus } from "lucide-react";
import DotGlobe from "../components/DotGlobe";
import EditorialMarquee from "../components/Marquee";
import StatCounter from "../components/StatCounter";
import { Reveal, MaskedLine, Stagger, StaggerItem } from "../components/Reveal";
import { useSEO } from "../hooks/useSEO";
import { api } from "../lib/api";
import { STATS, SERVICES, WHY_US, PROCESS_STEPS, CERTIFICATIONS, TESTIMONIALS, FAQS, MARQUEE_ITEMS } from "../data/site";

const CHAPTERS = [
  { num: "01", title: "Source", desc: "Verified manufacturers and farm clusters, matched to your exact specification." },
  { num: "02", title: "Verify", desc: "Audits, inspections and lab testing before a single dollar moves." },
  { num: "03", title: "Move", desc: "Sea, air and multimodal freight with live tracking across 40+ countries." },
  { num: "04", title: "Deliver", desc: "Documentation that clears first time, and delivery that lands on schedule." },
];

// ===== FIRST SCREEN (HERO) =====
// The top section with the big headline, trade video and floating cards.
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const globeY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    const tryPlay = () => document.querySelectorAll("video").forEach((v) => v.play().catch(() => {}));
    const t = setTimeout(tryPlay, 500);
    window.addEventListener("pointerdown", tryPlay, { once: true });
    window.addEventListener("scroll", tryPlay, { once: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("pointerdown", tryPlay);
      window.removeEventListener("scroll", tryPlay);
    };
  }, []);

  // Only download the hero video on the screen size that shows it (performance)
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return (
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero-section">
      <div className="bg-grid pointer-events-none absolute inset-0 opacity-70" />
      <div className="pointer-events-none absolute -left-40 top-1/4 h-[500px] w-[500px] rounded-full bg-blue-800/15 blur-[160px] dark:bg-blue-700/20" />
      <div className="pointer-events-none absolute right-0 top-0 h-[420px] w-[420px] rounded-full bg-teal-500/10 blur-[140px]" />

      <motion.div style={{ y: globeY, opacity: fade }} className="pointer-events-none absolute -right-[12%] top-1/2 hidden h-[880px] w-[880px] -translate-y-1/2 lg:block xl:-right-[6%]">
        <DotGlobe className="h-full w-full" />
      </motion.div>

      {/* ===== HERO TRADE VIDEO ===== */}
      {/* The ship video beside the headline. To change the video, replace the files in frontend/public/videos/ (keep the same file names). */}
      {isDesktop && (
      <motion.div
        initial={{ opacity: 0, x: 60, scale: 0.94 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        transition={{ delay: 2.2, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute right-[4%] top-[42%] z-10 hidden w-[440px] -translate-y-1/2 lg:block xl:w-[520px]"
        data-testid="hero-trade-video"
      >
        <div className="pointer-events-none absolute -inset-8 rounded-[3rem] bg-teal-500/15 blur-3xl" />
        <div className="glass animate-float-slow relative overflow-hidden rounded-[1.75rem] p-2 shadow-[0_40px_120px_-30px_rgba(2,6,23,0.8)]">
          <div className="relative overflow-hidden rounded-[1.35rem]">
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster="/videos/trade-hero-poster.jpg"
              className="aspect-video w-full object-cover"
              aria-label="Aerial drone footage of a container ship at a global trade port"
            >
              <source src="/videos/trade-hero.webm" type="video/webm" />
              <source src="/videos/trade-hero-720.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />
          </div>
        </div>
      </motion.div>
      )}

      {/* Floating trade animation elements */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.45, duration: 0.8 }}
        className="absolute right-[7%] top-[13%] z-10 hidden xl:block"
      >
        <div className="glass animate-float flex items-center gap-3 rounded-2xl px-5 py-3.5" data-testid="hero-chip-sea">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-700/30 to-teal-500/30">
            <Ship className="h-4 w-4 text-teal-400" />
          </span>
          <div>
            <p className="text-xs font-semibold text-white">Sea Freight</p>
            <p className="text-[10px] text-slate-400">40+ ports worldwide</p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.65, duration: 0.8 }}
        className="absolute right-[30%] top-[62%] z-10 hidden xl:block"
      >
        <div className="glass animate-float-slow flex items-center gap-3 rounded-2xl px-5 py-3.5" data-testid="hero-chip-air">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500/30 to-blue-700/30">
            <Plane className="h-4 w-4 text-sky-400" />
          </span>
          <div>
            <p className="text-xs font-semibold text-white">Air Cargo</p>
            <p className="text-[10px] text-slate-400">48h express lanes</p>
          </div>
        </div>
      </motion.div>

      <motion.div style={{ y: textY }} className="container-x relative z-10 pb-24 pt-36">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.7 }}
          className="glass mb-8 inline-flex items-center gap-2.5 rounded-full px-4 py-2"
          data-testid="hero-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-500" />
          </span>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Global Import • Export • Logistics</span>
        </motion.div>

        {/* ===== MAIN HEADLINE TEXT ===== */}
        {/* These 4 lines are the big headline. Edit the words inside each line. */}
        <h1 className="max-w-4xl font-display text-5xl font-bold leading-[1.02] tracking-tighter sm:text-7xl lg:text-[5.6rem]" data-testid="hero-headline">
          <MaskedLine delay={1.3}>Connecting</MaskedLine>
          <MaskedLine delay={1.43}>Opportunities</MaskedLine>
          <MaskedLine delay={1.56} className="text-gradient">Across Global</MaskedLine>
          <MaskedLine delay={1.69} className="text-gradient">Markets</MaskedLine>
        </h1>

        {/* ===== PARAGRAPH UNDER THE HEADLINE ===== */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9, duration: 0.8 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg"
        >
          Evolve Trade &amp; Logistics is your single accountable partner for sourcing, quality, documentation and delivery — moving commodities and products across 40+ countries with enterprise-grade reliability.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/contact" className="btn-primary" data-testid="hero-cta-quote">
            Start a Trade Inquiry <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/products" className="btn-secondary" data-testid="hero-cta-products">
            Explore Products
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.3, duration: 1 }}
          className="mt-14 flex flex-wrap gap-3"
        >
          {["IEC Registered", "APEDA", "FSSAI", "ISO 9001:2015"].map((b) => (
            <span key={b} className="chip" data-testid={`hero-cert-${b.toLowerCase().replace(/[\s.]+/g, "-")}`}>
              <ShieldCheck className="h-3.5 w-3.5 text-teal-500" /> {b}
            </span>
          ))}
        </motion.div>

        {/* Trade video — mobile/tablet (below content) */}
        {!isDesktop && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.45, duration: 0.8 }}
          className="mt-10 lg:hidden"
          data-testid="hero-trade-video-mobile"
        >
          <div className="glass relative overflow-hidden rounded-[1.5rem] p-2">
            <div className="relative overflow-hidden rounded-[1.1rem]">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster="/videos/trade-hero-poster.jpg"
                className="aspect-video w-full object-cover"
                aria-label="Aerial drone footage of a container ship at a global trade port"
              >
                <source src="/videos/trade-hero.webm" type="video/webm" />
                <source src="/videos/trade-hero-720.mp4" type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />
            </div>
          </div>
        </motion.div>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.55 }}
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="flex flex-col items-center gap-2 text-muted-foreground">
          <span className="text-[10px] font-semibold uppercase tracking-[0.3em]">Scroll</span>
          <ArrowDown className="h-4 w-4" />
        </motion.div>
      </motion.div>
    </section>
  );
}

// ===== ANIMATED NUMBERS SECTION =====
// The 4 counting numbers. The actual numbers are in src/data/site.js (HOMEPAGE NUMBERS).
function Stats() {
  return (
    <section className="py-20" data-testid="stats-section">
      <div className="container-x grid grid-cols-2 gap-10 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.1}>
            <StatCounter value={s.value} suffix={s.suffix} label={s.label} testId={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}

// ===== "EVOLVE DOCTRINE" SECTION =====
// The 4 numbered chapters (01 Source, 02 Verify, 03 Move, 04 Deliver).
// Edit the chapter text in the CHAPTERS list directly below.
function Manifesto() {
  return (
    <section className="py-24 sm:py-32" data-testid="manifesto-section">
      <div className="container-x">
        <div className="grid gap-16 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <Reveal>
              <p className="section-label">The Evolve Doctrine</p>
              <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
                Trade is won in the <span className="text-gradient">details</span> others skip.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                Anyone can quote a price. Few can verify a factory, inspect a lot, clear customs first time and land a container on schedule — repeatedly, across continents. That discipline is our entire business.
              </p>
              <Link to="/about" className="btn-secondary mt-8" data-testid="manifesto-about-link">
                Our Story <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <Stagger className="divide-y divide-slate-900/10 dark:divide-white/10">
            {CHAPTERS.map((c) => (
              <StaggerItem key={c.num}>
                <div className="group flex items-start gap-6 py-7 transition-[padding] duration-500 hover:pl-4" data-testid={`chapter-${c.num}`}>
                  <span className="font-display text-sm font-bold tracking-widest text-teal-500">{c.num}</span>
                  <div>
                    <h3 className="font-display text-2xl font-semibold tracking-tight transition-colors duration-300 group-hover:text-teal-500 sm:text-3xl">{c.title}</h3>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">{c.desc}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}

// ===== PRODUCT PORTFOLIO SECTION =====
// FEATURED_SLUGS = which products appear FIRST with a gold "Featured" badge.
// Add any product "slug" from backend/seed_data.py to feature it.
const FEATURED_SLUGS = ["basmati-rice", "thermal-coal", "dried-red-chilli"];

function ProductsPreview() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.products().then((p) => {
      const sorted = [...p].sort((a, b) => {
        const fa = FEATURED_SLUGS.indexOf(a.slug);
        const fb = FEATURED_SLUGS.indexOf(b.slug);
        return (fa === -1 ? 99 : fa) - (fb === -1 ? 99 : fb);
      });
      setProducts(sorted.slice(0, 8));
    }).catch(() => {});
  }, []);
  return (
    <section className="py-24 sm:py-32" data-testid="products-preview-section">
      <div className="container-x">
        <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="section-label">Product Portfolio</p>
            <h2 className="mt-4 max-w-xl font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
              Commodities &amp; products, <span className="text-gradient">export-ready</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/products" className="btn-secondary" data-testid="products-view-all">
              View Full Catalogue <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p) => (
            <StaggerItem key={p.slug}>
              <Link to={`/products/${p.slug}`} className="group block" data-testid={`product-card-${p.slug}`}>
                <div className="relative overflow-hidden rounded-2xl border border-slate-900/10 dark:border-white/10">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-transparent" />
                  <span className="glass absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-teal-300">{p.category}</span>
                  {FEATURED_SLUGS.includes(p.slug) && (
                    <span className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-950" data-testid={`featured-badge-${p.slug}`}>
                      <Star className="h-3 w-3 fill-slate-950" /> Featured
                    </span>
                  )}
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <h3 className="font-display text-lg font-semibold text-white">{p.name}</h3>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-300">
                      MOQ: {p.moq}
                      <ArrowRight className="h-3 w-3 text-teal-400 opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100" />
                    </p>
                  </div>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

// ===== SERVICES GRID SECTION =====
// Shows 6 of the 12 services. Service text is in src/data/site.js (SERVICES LIST).
function ServicesBento() {
  const featured = [SERVICES[0], SERVICES[1], SERVICES[5], SERVICES[7], SERVICES[10], SERVICES[11]];
  return (
    <section className="py-24 sm:py-32" data-testid="services-bento-section">
      <div className="container-x">
        <Reveal className="mb-14 max-w-2xl">
          <p className="section-label">What We Do</p>
          <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            One partner, <span className="text-gradient">every mile</span> of the journey
          </h2>
        </Reveal>
        <Stagger className="grid gap-6 md:grid-cols-3">
          {featured.map((s, i) => (
            <StaggerItem key={s.title} className={i === 0 || i === 5 ? "md:col-span-2" : ""}>
              <div className="card-surface group relative h-full overflow-hidden" data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-teal-500/10 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
                <s.icon className="h-9 w-9 text-teal-500" />
                <h3 className="mt-5 font-display text-xl font-semibold tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal className="mt-10 text-center">
          <Link to="/services" className="btn-secondary" data-testid="services-view-all">
            All 12 Trade Services <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

// ===== WHY CHOOSE US SECTION =====
// The 8 selling points. Text is in src/data/site.js (WHY CHOOSE US).
function WhyUs() {
  return (
    <section className="relative py-24 sm:py-32" data-testid="why-us-section">
      <div className="pointer-events-none absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-teal-500/40 to-transparent" />
      <div className="container-x">
        <Reveal className="mb-14 max-w-2xl">
          <p className="section-label">Why Evolve</p>
          <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            Built for buyers who <span className="text-gradient">cannot afford surprises</span>
          </h2>
        </Reveal>
        <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY_US.map((w) => (
            <StaggerItem key={w.title}>
              <div className="card-surface h-full !p-6" data-testid={`why-card-${w.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <w.icon className="h-7 w-7 text-teal-500" />
                <h3 className="mt-4 font-display text-base font-semibold">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

// ===== EXPORT PROCESS SECTION (AUTO-SCROLL + MANUAL DRAG) =====
// The 10 steps scroll by themselves AND can be dragged left/right manually.
// Step text is in src/data/site.js (EXPORT PROCESS STEPS).
// "0.6" in the useEffect = auto-scroll speed (higher = faster).
function Process() {
  const trackRef = useRef(null);
  const paused = useRef(false);
  const drag = useRef({ down: false, startX: 0, startScroll: 0 });

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let raf;
    const tick = () => {
      if (!paused.current && !drag.current.down) {
        el.scrollLeft += 0.6;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft = 0;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onPointerDown = (e) => {
    drag.current = { down: true, startX: e.clientX, startScroll: trackRef.current.scrollLeft };
  };
  const onPointerMove = (e) => {
    if (!drag.current.down) return;
    trackRef.current.scrollLeft = drag.current.startScroll - (e.clientX - drag.current.startX);
  };
  const endDrag = () => { drag.current.down = false; };

  const steps = [...PROCESS_STEPS, ...PROCESS_STEPS];

  return (
    <section className="py-24 sm:py-32" data-testid="process-section">
      <div className="container-x">
        <Reveal className="mb-14 max-w-2xl">
          <p className="section-label">The Export Process</p>
          <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            Ten steps. <span className="text-gradient">Zero ambiguity.</span>
          </h2>
        </Reveal>
      </div>
      <div className="[mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
        <div
          ref={trackRef}
          data-testid="process-timeline"
          className="cursor-grab select-none overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden active:cursor-grabbing"
          onPointerEnter={() => { paused.current = true; }}
          onPointerLeave={() => { paused.current = false; endDrag(); }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
        >
          <div className="flex w-max py-2 pl-5">
            {steps.map((step, i) => (
              <div key={i} className="mx-3 w-[290px] shrink-0">
                <div className="card-surface group relative h-full !p-6" data-testid={`process-step-${(i % 10) + 1}`}>
                  <span className="font-display text-4xl font-bold text-slate-900/10 transition-colors duration-500 group-hover:text-teal-500/30 dark:text-white/10">
                    {String((i % 10) + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 font-display text-lg font-semibold">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="container-x mt-6">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Auto-scrolling — drag or hover to take control</p>
      </div>
    </section>
  );
}

// ===== CERTIFICATIONS SECTION =====
// The 6 certification badges. Text is in src/data/site.js (CERTIFICATIONS).
function Certifications() {
  return (
    <section className="py-24 sm:py-28" data-testid="certifications-section">
      <div className="container-x">
        <Reveal className="mb-12 text-center">
          <p className="section-label justify-center">Certifications &amp; Compliance</p>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
            Registered. Licensed. <span className="text-gradient">Audited.</span>
          </h2>
        </Reveal>
        <Stagger className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-6">
          {CERTIFICATIONS.map((c) => (
            <StaggerItem key={c.code}>
              <div className="glass group flex h-full flex-col items-center rounded-2xl p-6 text-center transition-[transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-teal-500/50" data-testid={`cert-${c.code.toLowerCase()}`}>
                <span className="font-display text-2xl font-bold tracking-tight text-gradient">{c.code}</span>
                <span className="mt-2 text-xs font-semibold">{c.name}</span>
                <span className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">{c.desc}</span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

// ===== TESTIMONIALS SECTION =====
// Client quotes. Text is in src/data/site.js (TESTIMONIALS).
function Testimonials() {
  return (
    <section className="py-24 sm:py-32" data-testid="testimonials-section">
      <div className="container-x">
        <Reveal className="mb-14 max-w-2xl">
          <p className="section-label">Client Voices</p>
          <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            Trusted across <span className="text-gradient">six regions</span>
          </h2>
        </Reveal>
        <Stagger className="grid gap-6 md:grid-cols-2">
          {TESTIMONIALS.map((t) => (
            <StaggerItem key={t.name}>
              <figure className="card-surface h-full" data-testid={`testimonial-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="font-display text-5xl leading-none text-teal-500/40">"</span>
                <blockquote className="mt-2 text-base leading-relaxed text-foreground/90">{t.quote}</blockquote>
                <figcaption className="mt-6 flex items-center gap-4 border-t border-slate-900/10 pt-5 dark:border-white/10">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-blue-700 to-teal-500 font-display text-sm font-bold text-white">
                    {t.name.split(" ").map((n) => n[0]).join("")}
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}, {t.company} — {t.country}</p>
                  </div>
                </figcaption>
              </figure>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

// ===== FAQ SECTION =====
// Questions & answers. Text is in src/data/site.js (FAQ).
function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-24 sm:py-32" data-testid="faq-section">
      <div className="container-x grid gap-14 lg:grid-cols-[1fr_1.5fr]">
        <Reveal>
          <p className="section-label">FAQ</p>
          <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            Questions, <span className="text-gradient">answered</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground">
            Everything buyers usually ask before their first shipment. Something else on your mind? Our trade desk replies within 24 hours.
          </p>
          <Link to="/contact" className="btn-primary mt-8" data-testid="faq-contact-link">Ask a Question <ArrowRight className="h-4 w-4" /></Link>
        </Reveal>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="glass overflow-hidden rounded-2xl" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display text-base font-semibold">{f.q}</span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-900/10 text-teal-500 dark:border-white/10">
                    {open === i ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <p className="px-6 pb-6 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== BIG CALL-TO-ACTION BANNER =====
// The dark banner near the bottom: "Your next market is one conversation away".
function CtaBanner() {
  return (
    <section className="pb-24 pt-8 sm:pb-32" data-testid="cta-banner-section">
      <div className="container-x">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-teal-500/20 bg-gradient-to-br from-blue-900 via-navy to-slate-950 px-8 py-16 text-center sm:px-16 sm:py-20">
            <div className="bg-grid pointer-events-none absolute inset-0 opacity-40" />
            <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-[560px] -translate-x-1/2 rounded-full bg-teal-500/20 blur-[120px]" />
            <div className="relative">
              <Globe2 className="mx-auto h-10 w-10 text-teal-400" />
              <h2 className="mx-auto mt-6 max-w-2xl font-display text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl">
                Your next market is one conversation away
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-base text-slate-300">
                Tell us what you need to buy or sell. A dedicated trade specialist will respond with a detailed quotation within 24 hours.
              </p>
              <div className="mt-9 flex flex-wrap justify-center gap-4">
                <Link to="/contact" className="btn-primary" data-testid="cta-start-inquiry">Start an Inquiry <ArrowRight className="h-4 w-4" /></Link>
                <Link to="/services" className="btn-secondary !border-white/25 !text-white" data-testid="cta-explore-services">Explore Services</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  useSEO(
    "Evolve Trade & Logistics — Global Import, Export & Sourcing Partner",
    "Connecting opportunities across global markets. Sourcing, export, import, procurement, quality inspection and logistics across 40+ countries."
  );
  return (
    <>
      <Hero />
      <EditorialMarquee items={MARQUEE_ITEMS} />
      <Stats />
      <Manifesto />
      <ProductsPreview />
      <ServicesBento />
      <WhyUs />
      <Process />
      <Certifications />
      <Testimonials />
      <Faq />
      <CtaBanner />
    </>
  );
}
